import { Flex, Layout, Menu } from "antd"
import type { MenuProps } from "antd"
import { useTranslation } from "react-i18next"
import {
  LoginOutlined,
  HomeOutlined,
  DatabaseOutlined,
  LogoutOutlined,
} from "@ant-design/icons"
import { useLocation, useNavigate } from "react-router-dom"
import { Link } from "../Link"
import { ThemeSwitcher } from "../ThemeSwitcher"
import { useTheme } from "../../context/Theme"
import { getStorageVariable } from "../../utils/storage"
import { STORAGE_ENCRYPTED_DATA, STORAGE_SESSION_ACTIVE, WEB_PORTALS } from "../../constants"
import * as routes from "../../routes"
import { useEncryptedData } from "../../context/EncryptedData"
import { systemLogout } from "../../services/systemLogout"
import { Button } from "../Button"

type MenuItem = Required<MenuProps>["items"][number]

const { slovakFtNews, slovakCryptoNews } = WEB_PORTALS
const { HOME, LOGIN, BACKUP, SYSTEM_LOGIN } = routes

export const Header: React.FC = () => {
  const { isDarkMode, toggleTheme } = useTheme()
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  useEncryptedData() // subscribe to context so header re-renders after login
  const isSystemAuthenticated = getStorageVariable(STORAGE_SESSION_ACTIVE) === "true"
  const areFtnSkCredentialsDefined =
    getStorageVariable(`${slovakFtNews}${STORAGE_ENCRYPTED_DATA}`) !== ""
  const areKnSkCredentialsDefined =
    getStorageVariable(`${slovakCryptoNews}${STORAGE_ENCRYPTED_DATA}`) !== ""
  const isHomeDisabled = !(
    areFtnSkCredentialsDefined && areKnSkCredentialsDefined
  )
  const defaultSelectedKey =
    location.pathname === HOME
      ? "home"
      : location.pathname === BACKUP
        ? "backup"
        : "login"

  const handleLogout = async () => {
    await systemLogout()
    navigate(SYSTEM_LOGIN)
  }

  const items: MenuItem[] = [
    {
      label: <Link to={LOGIN} label={t("header.login")} />,
      key: "login",
      icon: <LoginOutlined />,
    },
    {
      label: (
        <Link to={HOME} label={t("header.home")} />
      ),
      key: "home",
      icon: <HomeOutlined />,
    },
    {
      label: (
        <Link
          to={BACKUP}
          label={t("header.backup")}
        />
      ),
      key: "backup",
      icon: <DatabaseOutlined />,
    },
  ]

  return (
    <Layout.Header style={{ background: "none" }}>
      <Flex align="center" justify="space-between">
        <Menu
          theme={isDarkMode ? "dark" : "light"}
          mode="horizontal"
          selectedKeys={[defaultSelectedKey]}
          items={isSystemAuthenticated && !isHomeDisabled ? items : []}
          style={{ background: "none", width: "100%" }}
        />
        <Flex align="center" gap="small">
          {isSystemAuthenticated && (
            <Button
              label={t("header.logout")}
              icon={<LogoutOutlined />}
              onClick={handleLogout}
            />
          )}
          <ThemeSwitcher isDarkMode={isDarkMode} onToggle={toggleTheme} />
        </Flex>
      </Flex>
    </Layout.Header>
  )
}
